import { stringify as stringifyQuery } from 'querystring';
import { Component } from 'preact';
import { Maybe } from '../../common/data.js';
import {
	apply,
	asMutable,
	ensure,
	isNone,
	keepExisting,
	maybe,
	objectEntries,
	Pair,
	sleep,
	throttle,
} from '../../common/util.js';
import { Builder } from './builder/builder.js';
import {
	initColorPreset,
	isCommandBasedGraphics,
	MainColorGroup,
	mainColorGroups,
	rgbToHex,
} from '../../common/data/colors.js';
import { applyMask, paintCommands } from '../lib/commands.js';
import { getProductTemplate } from '../lib/api.js';
import { getUploadPreviewURL, wrapImageUrl } from '../lib/images.js';

import pointInPolygon from 'point-in-polygon';
import { DesignPart, DesignParts, ProductTemplate } from '../../common/data/designs.js';
import { Icon } from './icons.js';
import { Point, ProductAreaSide, PublicProduct, WithFeatures } from '../../common/data/products.js';
import { PublicFlow } from '../../common/data/flows.js';
import { getContentUrlPrefix } from '../../common/data/images.js';

export const previewStackOffset = 0.3;

const dontClip = false;
const logoVersion = 3;

interface Dimensions {
	width: number;
	height: number;
}
interface ForceRect extends Dimensions {
	x?: number;
	y?: number;
}

interface DesignPreviewImage {
	element?: HTMLImageElement;
	type?: string;
	commands?: Uint8Array;
	fill?: string;
	imageData?: ImageData;
	width?: number;
	height?: number;
	src?: string;
	loading?: boolean;
}

export interface DesignPreviewProps {
	product: PublicProduct & WithFeatures;
	flow: PublicFlow;
	parts: DesignParts;
	side: string;
	light?: boolean;
	selected?: Maybe<string>;
	editable?: boolean;
	onClick?: () => void;
	builder?: Builder;
	onPointerMove?: (cx: number, cy: number) => void;
	onPointerDown?: (cx: number, cy: number, button: number) => void;
	startDrag?: (cb: (cx: number, cy: number) => void, end?: () => void) => void;
	selectPart?: (pid: Maybe<string>) => void;
	updatePart?: (pid: string, props: Partial<DesignPart>) => void;
	forcePortrait?: boolean;
	forceRect?: ForceRect;
	forceDPR?: number;
	forceInView?: boolean;
	showMask?: boolean;
	showAreas?: boolean;
	showWrapAxes?: boolean;
	skipLimits?: boolean;
}

interface StatePart extends DesignPart {
	image?: DesignPreviewImage;
	hitAreas?: {
		area: number[][];
		crop?: number[][];
	}[];
}
interface DesignPreviewState {
	canvasHeight?: number;
	painted?: boolean;
}

function updateSelectionRect(cctr: HTMLElement, { x, y, w, h, mh }: Record<'x' | 'y' | 'w' | 'h' | 'mh', number>) {
	
	const tx = Math.round((x || 0) * mh);
	const ty = Math.round((y || 0) * mh);
	
	cctr.style.width = `${Math.round(mh * w)}px`;
	cctr.style.height = `${Math.round(mh * h)}px`;
	cctr.style.transform = `translate(-50%, -50%) translate(${tx}px, ${ty}px)`;
	
}

function fixAspectRatio(nw: number, nh: number, part: StatePart): Pair<number> {
	const el = part.image?.element;
	if (!el || !el.naturalWidth || !el.naturalHeight) return [nw, nh];
	return [nw, nw * (el.naturalHeight / el.naturalWidth)];
}

function getPolyForSide(side: ProductAreaSide): Maybe<Pair<number>[]> {
	if (side.type == 'rect' && side.bottomRight && side.topLeft) return [
		side.topLeft,
		[side.bottomRight[0], side.topLeft[1]],
		side.bottomRight,
		[side.topLeft[0], side.bottomRight[1]],
	];
	return side.points;
}

const fetching = new Map<string, Promise<any>>();
function dedupeAsync<R>(key: string, action: () => Promise<R>): Promise<R> {
	const ex = fetching.get(key);
	if (ex) return ex;
	const promise = action().finally(() => fetching.delete(key));
	fetching.set(key, promise);
	return promise;
}
const fetchJson = (url: string) => dedupeAsync(url, () => fetch(url).then(r => r.json()));
const fetchBuffer = (url: string) => dedupeAsync(url, () => fetch(url).then(r => r.arrayBuffer()));

interface MaskCacheEntry {
	data?: Uint8ClampedArray;
	clip?: ImageBitmap;
	promise?: Promise<void>;
}

export class DesignPreview extends Component<DesignPreviewProps, DesignPreviewState> {

	parts?: StatePart[];
	nextParts?: StatePart[];

	updating = false;
	painted = false;
	
	canvas!: HTMLCanvasElement;
	ratio = 1;
	selection?: HTMLElement | null;
	rectCache?: DOMRect;
	timeout?: any;
	animating?: boolean;
	cleanupTimeout?: any;
	forceCanvas?: HTMLCanvasElement | null;
	forceRect?: ForceRect;
	forceDPR?: number;
	lastSide?: string;
	scrollParent?: HTMLElement;
	scrollParentRect?: DOMRect;
	scrollListener?: any;
	sibling?: DesignPreview;
	updateSkipped?: boolean;
	dragging?: boolean;
	colorCanvases: { [K in MainColorGroup]?: HTMLCanvasElement } = {};
	touchStarted?: { x: number; y: number; t: number };
	
	static maskCache: Record<string, MaskCacheEntry> = {};
	static tmpCanvas?: HTMLCanvasElement;
	static tmpCanvas2?: HTMLCanvasElement;
	static tmpClipCanvas?: HTMLCanvasElement;
	static productTemplateCache: Record<string, ProductTemplate> = {};
	static logoJson?: Maybe<Dimensions | Promise<Dimensions>>;
	
	imageErrors = 0;
	
	constructor(props: DesignPreviewProps) {
		super();
		this.forceRect = props.forceRect;
		this.forceDPR = props.forceDPR;
	}
	
	render(props: DesignPreviewProps, state: DesignPreviewState) {
	
		this.lastSide = props.side;
		const resizeMouse = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			this.startResize(e.target as HTMLElement, e.clientX, e.clientY);
		};
		const resizeTouch = (e: TouchEvent) => {
			e.preventDefault();
			e.stopPropagation();
			this.startResize(e.target as HTMLElement, e.targetTouches[0]!.clientX, e.targetTouches[0]!.clientY);
		};
		const resizer = (mx: number, my: number) => <div
			className="design-preview__selection-resizer"
			style={{ [my < 0 ? 'top' : 'bottom']: '-4px', [mx < 0 ? 'left' : 'right']: '-4px' }}
			data-x={mx}
			data-y={my}
			onMouseDown={resizeMouse}
			onTouchStart={resizeTouch}
		/>;
		const is = props.product.features?.imageSizes?.[0] || [768, 1024];
		const orient = props.forcePortrait || is[0] < is[1] ? 'portrait' : 'landscape';
		const maskImage = props.product.features?.maskImage;
		
		return <div
			className={`design-preview design-preview--${orient} design-preview--${props.side}`}
			onClick={props.onClick}
			onMouseMove={props.onPointerMove && (e => this.handlePointerMove(e.clientX, e.clientY))}
			onMouseDown={(props.editable || props.onPointerDown) && (e => {
				e.preventDefault();
				this.checkHit(e.clientX, e.clientY, e.button);
			}) || undefined}
			onTouchStart={e => this.touchStarted = { x: e.changedTouches[0]!.clientX, y: e.changedTouches[0]!.clientY, t: Date.now() }}
			onTouchMove={props.onPointerMove && (e => this.handlePointerMove(e.touches[0]!.clientX, e.touches[0]!.clientY))}
			onTouchEnd={props.editable && (e => this.checkHit(e.changedTouches[0]!.clientX, e.changedTouches[0]!.clientY)) || undefined}
		>
			<canvas ref={el => el && (this.canvas = el)} style={state.canvasHeight ? { height: state.canvasHeight + 'px' } : {}} />
			{props.selected && maybe(
				props.parts[props.selected],
				part => (!part.point?.side || part.point.side == props.side) && <div
					key={`selected-${part.id}`}
					class="design-preview__selection"
					ref={el => this.selection = el}
					onMouseDown={e => {
						e.preventDefault();
						e.stopPropagation();
						this.startDrag(e.target as HTMLElement, e.clientX, e.clientY);
					}}
					onTouchStart={e => {
						e.preventDefault();
						e.stopPropagation();
						this.startDrag(e.target as HTMLElement, e.targetTouches[0]!.clientX, e.targetTouches[0]!.clientY);
					}}
					style={{ display: 'none' }}
				>
					{resizer( -1, -1)}
					{resizer(1, -1)}
					{resizer(1, 1)}
					{resizer(-1, 1)}
					{maybe(props.builder, b => <div className="design-preview__selection-actions">{
						!b.state.zoomArea
							? <button onMouseDown={this.zoomToSelection} onTouchStart={this.zoomToSelection}><Icon name="zoom-in" /></button>
							: <button onMouseDown={this.resetZoom} onTouchStart={this.resetZoom}><Icon name="zoom-out" /></button>
					}</div>)}
				</div>
			)}
			{maskImage && state.painted && <img class="product-shadow" src={wrapImageUrl(`/data/product-masks/${maskImage}.${props.side}.shadow.png`)} />}
		</div>;
		
	}
	
	componentDidMount() {
		this.scrollListener = throttle(() => {
			this.updateRectCache();
			const inView = this.isInView();
			if (inView && this.updateSkipped) this.scheduleUpdate();
			if (!inView) this.cleanUp();
		}, 100);
		this.scrollParent = (this.base as HTMLElement).closest('.design-preview-container') as HTMLElement || document.body;
		this.scrollParent.addEventListener('scroll', this.scrollListener);
		this.updateRectCache();
		this.update();
	}
	
	componentWillUnmount() {
		this.scrollParent?.removeEventListener('scroll', this.scrollListener);
	}

	shouldComponentUpdate(props: DesignPreviewProps, state: DesignPreviewState) {
		
		if (this.props.product != props.product) {
			this.setState({ canvasHeight: undefined });
			return true;
		}
		if (this.lastSide != props.side) return true;
		if (this.rectCache?.height != state.canvasHeight) return true;
		if (this.state.painted != state.painted) return true;
		
		if (this.props.selected !== props.selected) return true;
		if (!this.parts) return true;
		for (const pid of Object.keys(props.parts)) if (!this.parts.find(p => p.id == pid)) return true;
		for (const part of this.parts) if (!props.parts[part.id] || part.point?.side != props.parts[part.id]!.point?.side || (part.type == 'apparel' && part.productTemplate != props.parts[part.id]!.productTemplate)) return true;
		
		if (this.updating) return false;
		
		this.updateRectCache();
		this.scheduleUpdate();
		
		return false;
		
	}
	
	componentDidUpdate() {
		this.updateRectCache();
		this.scheduleUpdate();
	}

	componentWillReceiveProps(props: DesignPreviewProps) {
		this.scheduleUpdate();
	}
	
	updateRectCache() {
		this.rectCache = (this.canvas).getBoundingClientRect();
		this.scrollParentRect = this.scrollParent?.getBoundingClientRect();
	}

	isInView() {
		return this.props.forceInView || (
			this.rectCache!.top - Math.max(this.scrollParentRect!.height / 2, 500) <= this.scrollParentRect!.bottom
			&& this.rectCache!.bottom + Math.max(this.scrollParentRect!.height / 2, 500) >= this.scrollParentRect!.top
		);
	}
	
	scheduleUpdate() {
		if (!this.timeout) this.timeout = setTimeout(() => this.update(), 100);
	}
	scheduleAnimation() {
		if (this.animating) return;
		requestAnimationFrame(this.paintOnAnimation);
		this.animating = true;
	}
	paintOnAnimation = () => {
		this.animating = false;
		this.paint();
		if (this.dragging && this.sibling) this.sibling.paint();
	};
	
	imgToImgData(element: HTMLImageElement, cw: number, ch: number, cover = false) {
		const tmp = document.createElement('canvas');
		tmp.width = cw;
		tmp.height = ch;
		const ctx = tmp.getContext('2d')!;
		const [tw, th] = (element.naturalWidth / cw > element.naturalHeight / ch) == !cover ? [cw, element.naturalHeight * (cw / element.naturalWidth)] : [element.naturalWidth * (ch / element.naturalHeight), ch];
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';
		ctx.drawImage(element, (cw - tw) / 2, (ch - th) / 2, tw, th);
		return ctx.getImageData(0, 0, cw, ch);
	}

	prepareImage(src: string, prev?: DesignPreviewImage) {
		
		const url = src.includes('://') ? src : location.protocol + '//' + location.host + src;

		let img: DesignPreviewImage;
		let promise: Promise<void> | undefined;
		if (prev && prev.element && prev.element.src == url && prev.element.naturalWidth && prev.element.naturalHeight) {
			img = prev;
		}
		else {
			const element = document.createElement('img');
			element.crossOrigin = 'anonymous';
			promise = new Promise<any>(resolve => {
				element.onload = resolve;
				element.onerror = () => setTimeout(resolve, 250 * this.imageErrors++);
				element.src = url;
			});
			img = { element };
		}

		return { img, promise };

	}
	
	prepareCommands(src: string, dimensions: ({ width: number; height: number }) | Promise<({ width: number; height: number })>, prevImg?: DesignPreviewImage) {
	
		if (prevImg && prevImg.src == src) {
			return { img: prevImg };
		}

		const img: DesignPreviewImage = {
			type: 'commands',
			src,
		};

		const promise = (async () => {
			const { width, height } = await dimensions;
			img.width = width;
			img.height = height;
			const res = await fetchBuffer(src);
			img.commands = new Uint8Array(res);
		})();
		
		return { promise, img };
		
	}
	
	getDPR() {
		return this.forceDPR || (this.props.light ? 1 : devicePixelRatio);
	}
	
	async update(force = false, parts = this.props.parts) {
	
		this.timeout = undefined;
		if (this.updating) return;
		
		if (!this.base) return;
		const props = this.props;
		
		// DesignPreview cannot be used without a productTemplate set.
		// This can only happen when not using DesignManager (it fixes this) so just patch in place.
		if (!Object.values(parts).some(part => part.productTemplate)) {
			const patch = Object.values(parts).find(part => part.productDesign);
			if (patch) asMutable(patch).productTemplate = patch.productDesign;
			else return;
		}
		
		if (!force && !this.isInView()) {
			this.updateSkipped = true;
			return;
		}
		
		const br = this.forceRect || this.rectCache!;
		if (!br.width) {
			this.updateSkipped = true;
			return;
		}

		this.updateSkipped = false;
		this.updating = true;
		
		const pr = this.getDPR();
		const cw = Math.round(br.width * pr);
		const ch = Math.round(br.width / this.ratio * pr);
		
		const features = props.product.features!;
		const imageSizes = features.imageSizes!;
		const imageSize = imageSizes.slice().reverse().find(isc => isc[0] >= cw && isc[1] >= ch) || imageSizes[0]!;
		const [dw, dh] = imageSize;

		this.ratio = dw / dh;
		
		// Patch in place, not great.
		const partsArray = Object.values(parts);
		for (const part of partsArray) {
			if (part.productTemplate && !part.productTemplate.type) {
				if (!DesignPreview.productTemplateCache[part.productTemplate.id]) {
					const res = await getProductTemplate({ id: part.productTemplate.id });
					DesignPreview.productTemplateCache[part.productTemplate.id] = res.productTemplate!;
				}
				asMutable(part).productTemplate = DesignPreview.productTemplateCache[part.productTemplate.id];
			}
		}
		
		const promises: (Promise<any> | undefined)[] = [];
		
		const mcpfx = `${props.product.id}_${props.side}`;
		const mcid = `${mcpfx}_${cw}_${ch}`;
		if (features.mask && !DesignPreview.maskCache[mcid]) {

			const mce: MaskCacheEntry = {};
			mce.promise = (async () => {
				const element = document.createElement('img');
				element.crossOrigin = 'anonymous';
				await new Promise<any>(resolve => {
					element.onload = resolve;
					element.onerror = () => setTimeout(resolve, 250 * this.imageErrors++);
					element.src = `/data/product-masks/${features.maskImage || props.product.id}.${props.side}.png`;
				});
				const mid = this.imgToImgData(element, cw, ch);
				const mdata = mce.data = mid.data;
				if (features.maskFeatures?.clip) {
					const clip = new ImageData(mid.width, mid.height);
					for (let i = 0, len = clip.data.length; i < len; i += 4) {
						clip.data[i + 3] = mdata[i + 2]!;
					}
					mce.clip = await createImageBitmap(clip);
				}
			})();
			DesignPreview.maskCache[mcid] = mce;
			
		}
		promises.push(DesignPreview.maskCache[mcid]?.promise);
		
		const nextParts = partsArray.map((part) => {
		
			let prevPart: StatePart | undefined;
			if (this.nextParts) prevPart = this.nextParts.find(p => p.id == part.id);
			else if (this.parts) prevPart = this.parts.find(p => p.id == part.id);
			if (prevPart && prevPart.id != part.id) prevPart = undefined;
			
			const hitAreas = prevPart?.hitAreas;
			const prevImg = prevPart && prevPart.image;
			
			const design = part.productTemplate;
			
			if (design && design.texture_mask) {
				const mkey = `tx_${design.texture_mask}_${cw}_${ch}`;
				if (!DesignPreview.maskCache[mkey]) {
					const mce: MaskCacheEntry = {};
					mce.promise = (async () => {
						const element = document.createElement('img');
						element.crossOrigin = 'anonymous';
						await new Promise<any>(resolve => {
							element.onload = resolve;
							element.onerror = () => setTimeout(resolve, 250 * this.imageErrors++);
							element.src = `/static/textures/${design.texture_mask}.png.webp`;
						});
						const mid = this.imgToImgData(element, cw, ch, true);
						const mdata = mce.data = mid.data;
						if (features.maskFeatures?.clip) {
							const clip = new ImageData(mid.width, mid.height);
							for (let i = 0, len = clip.data.length; i < len; i += 4) {
								clip.data[i + 3] = mdata[i + 2]!;
							}
							mce.clip = await createImageBitmap(clip);
						}
					})();
					DesignPreview.maskCache[mkey] = mce;
				}
				promises.push(DesignPreview.maskCache[mkey].promise);
			}
			
			if (design && design.type == 'commands') {

				const src = design.commands![`${props.side}_${dw}_${dh}`];
				
				const { img, promise } = this.prepareCommands(`/data/product-designs/${src}`, { width: dw, height: dh }, prevImg);
				if (promise) promises.push(promise);
				
				return { ...part, hitAreas, image: img };
				
			}
			else if (design && features.maskFeatures?.commands) {

				const src = `/data/product-masks/${features.maskImage || props.product.id}.${props.side}.${dw}x${dh}.dat`;
				
				const { img, promise } = this.prepareCommands(src, { width: dw, height: dh }, prevImg);
				if (promise) promises.push(promise);
				
				return { ...part, hitAreas, image: img };
				
			}
			else if (design && design.type == 'texture') {
				
				const { img, promise } = this.prepareImage(wrapImageUrl(`/data/product-designs/${design.texture}`), prevImg);
				promises.push(promise);
				
				return { ...part, hitAreas, image: img };
				
			}
			else if (design && design.type == 'solid') {
				
				return {
					...part,
					hitAreas,
					image: {
						fill: `rgba(${design.color?.join(',')},255)`,
						width: dw,
						height: dh,
					},
				};
				
			}
			else if (part.upload && isCommandBasedGraphics(props.flow) && part.vector_upload?.commands) {
				
				const vu = part.vector_upload;
				const { img, promise } = this.prepareCommands(
					vu.commands!,
					{ width: vu.commands_width!, height: vu.commands_height! },
					prevImg,
				);
				if (promise) promises.push(promise);
				
				return { ...part, hitAreas, image: img };
				
			}
			else if (part.upload) {
			
				const preview = getUploadPreviewURL(part.upload, props.flow.color_profile || 'rgb');
				
				const { img, promise } = this.prepareImage(preview, prevImg);
				if (isCommandBasedGraphics(props.flow)) img.loading = true;
				promises.push(promise);

				return { ...part, hitAreas, image: img };

			}
			else if (part.flag && part.flag.country) {
				
				const { img, promise } = this.prepareImage('/data/flags/' + part.flag.country + '.png', prevImg);
				promises.push(promise);

				return { ...part, hitAreas, image: img };

			}
			else if (part.type == 'pw-logo') {
				
				const domain = part.domain ?? 'pw';
				if (!DesignPreview.logoJson) DesignPreview.logoJson = (async () => {
					DesignPreview.logoJson = ensure(await fetchJson(`${getContentUrlPrefix()}/static/logo/${domain}!${logoVersion}.json`)) as Dimensions;
					return DesignPreview.logoJson;
				})();
				const { img, promise } = this.prepareCommands(
					`${getContentUrlPrefix()}/static/logo/${domain}!${logoVersion}.dat`,
					DesignPreview.logoJson,
					prevImg
				);
				if (promise) promises.push(promise);
				
				return { ...part, hitAreas, image: img };

			}
			else if (part.text) {
			
				if (!part.text.font) return { ...part, hitAreas, images: [] };
				
				const ps = maybe(features.physicalScale, s => s.inches / s.per);
				const size = ps && part.text.sizeIn ? Math.round(part.text.sizeIn / ps * dh) : '';
				const width = Math.round((part.point?.w ?? 1) * dh);
				const height = Math.round((part.point?.h ?? 1) * dh);
				
				const qs = stringifyQuery({
					font: part.text.font,
					fill: part.text.fill && maybe(part.colors?.fill, rgbToHex) || '',
					stroke: part.text.stroke && maybe(part.colors?.stroke, rgbToHex) || '',
					text: part.text.text,
					size,
					width,
					height,
					rotate: part.text.rotate || 0,
					v: 2,
				});
				const url = `/render-text?${qs}`;

				const { img, promise } = this.prepareImage(url, prevImg);
				promises.push(promise);

				return { ...part, hitAreas, image: img };
			
			}
			

			return { ...part, hitAreas, image: undefined };
			
			
			
		});
		
		this.nextParts = nextParts;
		await Promise.all(promises);
		this.updating = false;
		
		if (this.nextParts != nextParts) throw new Error('DesignPreview.update() raced?');
		
		this.painted = false;
		this.parts = nextParts;
		this.paint();
		
		if (this.cleanupTimeout) clearTimeout(this.cleanupTimeout);
		this.cleanupTimeout = setTimeout(() => this.cleanUp(), 60000);
		
	}
	
	cleanUp() {
		if (this.parts) for (const part of this.parts) {
			if (!part.image) continue;
			delete part.image.imageData;
		}
	}
	
	sortParts<T extends (StatePart | DesignPart)>(parts: T[], maskedFirst = false) {
		const rank = (part: T) => {
			if (maskedFirst && part.drawOverColor) return -2000 - parts.indexOf(part);
			if (part.type == 'apparel') return -1000;
			if (part.type == 'pw-logo') return 1000;
			return -1 - (part.sort || 0);
		};
		const copy = parts.slice();
		copy.sort((a, b) => rank(a) - rank(b));
		return copy;
	}
	
	paint() {
	
		const props = this.props;
		
		const br = this.forceRect || this.rectCache!;
		if (!br.width) return;
		
		const cph = Math.round(br.width / this.ratio);
		if (cph != this.state.canvasHeight && !this.forceRect) return this.setState({ canvasHeight: cph });
		
		const pr = this.getDPR();
		const mw = br.width;
		const mh = br.width / this.ratio;
		const cw = Math.round(mw * pr);
		const ch = Math.round(mh * pr);
		
		if (!DesignPreview.tmpCanvas) DesignPreview.tmpCanvas = document.createElement('canvas');
		const canvas = DesignPreview.tmpCanvas;
		canvas.width = cw;
		canvas.height = ch;
		const mctx = canvas.getContext('2d')!;
		mctx.imageSmoothingEnabled = true;
		mctx.imageSmoothingQuality = 'high';
		
		if (!DesignPreview.tmpCanvas2) DesignPreview.tmpCanvas2 = document.createElement('canvas');
		const tmpCanvas = DesignPreview.tmpCanvas2;
		const tctx = tmpCanvas.getContext('2d')!;
		
		const { features, id } = props.product;
		const haveMask = features?.mask;
		const mdata = DesignPreview.maskCache[`${id}_${props.side}_${cw}_${ch}`]?.data;
		const clipBmp = DesignPreview.maskCache[`${id}_${props.side}_${cw}_${ch}`]?.clip;
		if (haveMask && !mdata) {
			this.scheduleUpdate();
			return;
		}
		
		if (!DesignPreview.tmpClipCanvas) DesignPreview.tmpClipCanvas = document.createElement('canvas');
		const clipCanvas = DesignPreview.tmpClipCanvas;
		const cctx = clipCanvas.getContext('2d')!;
		if (clipBmp) {
			clipCanvas.width = cw;
			clipCanvas.height = ch;
		}
		
		let cancel = false;

		const parts = this.sortParts(this.parts!, true);
		
		const apparel = parts.find(p => p.type == 'apparel')!;
		if (!apparel.image) {
			this.scheduleUpdate();
			return;
		}
		
		const txMaskId = apparel.productTemplate?.texture_mask;
		const tdata = isNone(txMaskId) ? null : DesignPreview.maskCache[`tx_${txMaskId}_${cw}_${ch}`]?.data;
		if (txMaskId && !tdata) {
			this.scheduleUpdate();
			return;
		}
		
		const prepareColorCanvas = (cid: MainColorGroup, canvas: HTMLCanvasElement) => {
			canvas.width = apparel.image!.width!;
			canvas.height = apparel.image!.height!;
			const ctx = canvas.getContext('2d')!;
			const pt = apparel.productTemplate;
			ctx.fillStyle = `rgba(${
				(
					(pt?.type == 'solid' && cid == 'base' ? pt.color : null)
					|| pt?.color_lock?.[cid]
					|| apparel.colors?.[cid]
					|| pt?.template_info?.colors?.[cid]
					|| initColorPreset[mainColorGroups.indexOf(cid)]!
				).slice(0, 3).join(',')
			}, 255)`;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			return ctx;
		};
		for (const [cid, canvas] of objectEntries(this.colorCanvases)) {
			if (parts.some(part => part.drawOverColor == cid)) {
				prepareColorCanvas(cid, ensure(canvas));
			}
			else {
				delete this.colorCanvases[cid];
			}
		}
		
		for (const part of parts) {
		
			if (cancel) return;
			
			let ctx: CanvasRenderingContext2D;
			let tcw = cw, tch = ch;
			const mcid = part.drawOverColor;
			if (mcid) {
				if (!this.colorCanvases[mcid]) {
					const canvas = document.createElement('canvas');
					this.colorCanvases[mcid] = canvas;
					ctx = prepareColorCanvas(mcid, canvas);
					ctx.imageSmoothingEnabled = true;
					ctx.imageSmoothingQuality = 'high';
				}
				else {
					ctx = ensure(this.colorCanvases[mcid].getContext('2d'));
				}
				tcw = ctx.canvas.width;
				tch = ctx.canvas.height;
			}
			else if (clipBmp && part.type != 'apparel') {
				ctx = cctx;
				ctx.clearRect(0, 0, cw, ch);
			}
			else {
				ctx = mctx;
			}
			
			part.hitAreas = [];
			
			const drawPoint = (point?: Point, sleeve?: 'left' | 'right') => {

				if (point?.left || point?.right) {
					if (point.left) drawPoint({ ...point, left: undefined, right: undefined, ...point.left }, 'left');
					if (point.right) drawPoint({ ...point, left: undefined, right: undefined, ...point.right }, 'right');
					return;
				}

				const [pw, ph, px, py] = point?.cover ? [this.ratio, 1, 0, 0] : [point?.w || this.ratio, point?.h || 1, point?.x || 0, point?.y || 0];

				const area = point?.area ? features?.areas?.[point.area] : undefined;
				const poly = maybe(area?.sides[props.side], getPolyForSide);
				if (point?.area && !poly && point.area != 'admin') {
					return;
				}

				const targetAndDraw = (px: number, py: number, main = true) => {
					
					let crop;
					if (poly && !dontClip && part.cropToArea !== false) {
						crop = this.drawPolygon(ctx, poly, tcw, tch);
						ctx.clip();
					}

					ctx.translate(
						tcw / 2 + px * tch,
						tch / 2 + py * tch,
					);
					const rot = area?.sides[props.side]?.rotate;
					if (rot) ctx.rotate(Math.PI * rot);

					const img = part.image;
					if (!img) return;
					if ((!img.element && !img.commands && !img.fill)) {
						this.scheduleUpdate();
						cancel = true;
						return;
					}

					const iw = img.width || img.element?.naturalWidth;
					const ih = img.height || img.element?.naturalHeight;
					if (!iw || !ih) {
						this.scheduleUpdate();
						cancel = true;
						return;
					}

					const [dw, dh] = pw / iw < ph / ih ? [pw * tch, ih * (pw / iw) * tch] : [iw * (ph / ih) * tch, ph * tch];
					const [dx, dy] = [-dw / 2, -dh / 2];
					
					const tm = ctx.getTransform();
					const txy = (x: number, y: number) => [
						tm.a * x + tm.c * y + tm.e,
						tm.b * x + tm.d * y + tm.f,
					];
					part.hitAreas!.push({
						area: [
							txy(dx, dy),
							txy(dx + dw, dy),
							txy(dx + dw, dy + dh),
							txy(dx, dy + dh),
						],
						crop,
					});
					
					if (part.flag && sleeve) {
						
						if (part.flag[sleeve] == 'off') return;
						if (parts.find(p => p.type == 'pw-logo')?.position == `pw-${sleeve}-sleeve`) return;

					}
					
					if (part.flag?.outline) {
						const ow = dw * 1.2;
						const oh = dh * 1.2;
						ctx.lineWidth = 0.002 * tch;
						ctx.strokeStyle = rgbToHex(part.colors!.outline!);
						ctx.strokeRect(-ow * 0.5, -oh * 0.5, ow, oh);
					}
					
					if (img.loading) {
						ctx.globalAlpha = 0.5;
						ctx.filter = 'grayscale(0.5)';
					}
					
					if (img.element) {
					
						ctx.drawImage(img.element, dx, dy, dw, dh);
						
					}
					else if (img.fill) {
					
						ctx.fillStyle = img.fill;
						ctx.fillRect(dx, dy, dw, dh);
						
					}
					else if (img.commands) {
					
						tmpCanvas.width = img.width!;
						tmpCanvas.height = img.height!;
						
						let colors: (number[] | null)[];
						if (part.type == 'pw-logo') {
							colors = [part.colors?.logo || [255, 255, 255]];
						}
						else if (part.vector_upload?.commands_palette) {
							colors = part.vector_upload.commands_palette.map((rgb, cidx) => apply(
								part.colors?.[cidx + 1],
								c => c || c === null ? c : rgb as number[]
							));
						}
						else {
							const pt = part.productTemplate;
							colors = mainColorGroups.map(
								(cid, cidx) => (
									(pt?.type == 'solid' && cid == 'base' ? pt.color : null)
									|| pt?.color_lock?.[cid]
									|| part.colors?.[cid]
									|| part.productTemplate?.template_info?.colors?.[cid]
									|| initColorPreset[cidx]!
								)
							);
						}
						
						if (!img.imageData || img.imageData.width != img.width || img.imageData.height != img.height) {
							img.imageData = tctx.createImageData(img.width!, img.height!);
						}
						const patterns = part.type == 'apparel' ? mainColorGroups.map(cid => this.colorCanvases[cid]?.getContext('2d')!.getImageData(0, 0, img.width!, img.height!).data) : undefined;
						paintCommands(img.commands, img.imageData.data, colors, patterns);
						tctx.putImageData(img.imageData, 0, 0);
						ctx.drawImage(tmpCanvas, dx, dy, dw, dh);
						
					}
					
					if (img.loading) {
						ctx.globalAlpha = 1;
						ctx.font = '16px Play';
						ctx.fillText('Processing', dx + dw * 0.5 - ctx.measureText('Processing').width / 2, dy + dh * 0.5);
					}

					if (
						main
						&& props.selected == part.id
						&& this.selection
						&& !this.dragging
						&& !part.point?.cover
						&& (!part.point?.side || part.point.side == props.side)
						&& !['apparel', 'pw-logo', 'flag'].includes(part.type)
					) {
						this.selection.style.transform = `translate(-50%, -50%) translate(${Math.round(px * mh)}px, ${Math.round(py * mh)}px)`;
						const [sdw, sdh] = pw / iw < ph / ih ? [pw * ch, ih * (pw / iw) * ch] : [iw * (ph / ih) * ch, ph * ch];
						this.selection.style.width = `${sdw / pr}px`;
						this.selection.style.height = `${sdh / pr}px`;
						this.selection.style.display = 'block';
					}

				};
				
				if (point?.side && point.side != props.side) {
				
					if (part.wrap == false || part.tile) return;

					const oside = props.side == 'front' ? 'back' : 'front';
					const waxes = area?.sides[props.side]?.wrapAxes;

					if (waxes) for (let wi = 0; wi < waxes.length; wi++) {

						const wax = waxes[wi];
						const owax = area.sides[oside]?.wrapAxes?.[wi];
						if (!wax || !owax || !point || !point.x || !point.y) {
							ctx.restore();
							continue;
						}
						const yp = (point.y - owax[0][1]) / (owax[1][1] - owax[0][1]);
						const oxt = owax[0][0] + (owax[1][0] - owax[0][0]) * yp;
						const xt = wax[0][0] + (wax[1][0] - wax[0][0]) * yp;
						const xp = (point.x - oxt) / (owax[1][1] - owax[0][1]);
						const px = xt + xp * (wax[1][1] - wax[0][1]);
						const py = wax[0][1] + yp * (wax[1][1] - wax[0][1]);

						ctx.save();
						targetAndDraw(px, py);
						ctx.restore();

					}

				}
				else if (!part.tile) {
					ctx.save();
					targetAndDraw(px, py);
					ctx.restore();
				}
				else {
					const scpx = part.tile != 'vertical' ? px - Math.ceil((this.ratio / 2 + px) / pw) * pw : px;
					const ecpx = part.tile != 'vertical' ? this.ratio / 2 : px;
					const scpy = part.tile != 'horizontal' ? py - Math.ceil((0.5 + py) / ph) * ph : py;
					const ecpy = part.tile != 'horizontal' ? 0.5 : py;
					for (let cpx = scpx; cpx <= ecpx; cpx += pw) {
						for (let cpy = scpy; cpy <= ecpy; cpy += ph) {
							ctx.save();
							targetAndDraw(cpx, cpy, cpx == px && cpy == py);
							ctx.restore();
						}
					}
				}

			};
			
			drawPoint(part.point);
			
			if (ctx == cctx) {
				cctx.globalCompositeOperation = 'destination-out';
				cctx.drawImage(clipBmp!, 0, 0);
				cctx.globalCompositeOperation = 'source-over';
				mctx.drawImage(clipCanvas, 0, 0);
			}
			
		}
		
		let destCanvas = this.forceCanvas;
		if (!destCanvas) {
			destCanvas = this.canvas;
			destCanvas.width = cw;
			destCanvas.height = ch;
		}
		const dctx = destCanvas.getContext('2d')!;
		const dx = this.forceRect?.x || 0, dy = this.forceRect?.y || 0;
		
		if (mdata) {
			const idata = mctx.getImageData(0, 0, cw, ch);
			if (tdata) applyMask(idata.data, tdata, apparel.productTemplate?.texture_multiplier ?? 1, false);
			applyMask(idata.data, mdata, 1, !dontClip);
			dctx.putImageData(idata, dx, dy);
		}
		else {
			dctx.drawImage(canvas, dx, dy);
		}

		dctx.save();
		dctx.lineWidth = 3;
		dctx.strokeStyle = '#ff00ff';
		if (features?.areas) for (const area of Object.values(features.areas)) {
			
			if (props.showAreas) {
				dctx.strokeStyle = '#ff00ff';
				const poly = maybe(area.sides[props.side], getPolyForSide);
				if (poly) this.drawPolygon(dctx, poly, cw, ch);
				dctx.stroke();
			}
			
			if (props.showWrapAxes) {
				dctx.strokeStyle = '#00ffff';
				const waxes = area.sides[props.side]?.wrapAxes;
				if (waxes) for (const wax of waxes) {
					this.drawPolygon(dctx, wax, cw, ch);
					dctx.stroke();
				}
			}
			
		}
		if (props.showAreas) {
			if (features?.physicalScale) {
				dctx.strokeStyle = '#999900';
				dctx.beginPath();
				const cx = cw / 2;
				const hw = features.physicalScale.per / 2 * ch;
				dctx.moveTo(cx - hw, ch - 10);
				dctx.lineTo(cx - hw, ch);
				dctx.lineTo(cx + hw, ch);
				dctx.lineTo(cx + hw, ch - 10);
				dctx.stroke();
			}
			dctx.lineWidth = 1;
			dctx.setLineDash([5, 5]);
			dctx.strokeStyle = '#666';
			if (features?.points) for (const point of keepExisting(Object.values(features.points))) {
				if (point.side != props.side) continue;
				dctx.strokeRect(
					cw / 2 + point.x! * ch - point.w! * ch * 0.5,
					ch / 2 + point.y! * ch - point.h! * ch * 0.5,
					point.w! * ch,
					point.h! * ch,
				);
			}
		}
		dctx.restore();
		
		this.painted = true;
		if (!this.state.painted) this.setState({ painted: true });
		
	}
	
	drawPolygon(ctx: CanvasRenderingContext2D, poly: Pair<number>[], mw: number, mh: number) {
		const [cx, cy] = [mw / 2, mh / 2];
		const [x, y] = poly[poly.length - 1]!;
		ctx.beginPath();
		ctx.moveTo(cx + mh * x, cy + mh * y);
		return poly.map(([x, y]) => {
			const tx = cx + mh * x;
			const ty = cy + mh * y;
			ctx.lineTo(tx, ty);
			return [tx, ty];
		});
	}
	
	handlePointerMove(x: number, y: number) {
		const br = this.rectCache!;
		const mw = br.width;
		const mh = br.width / this.ratio;
		this.props.onPointerMove?.((x - br.left - mw / 2) / mh, (y - br.top - mh / 2) / mh);
	}
	
	checkHit(x: number, y: number, button = 0) {
		
		const ts = this.touchStarted;
		this.touchStarted = undefined;
		if (ts && (
			Date.now() - ts.t > 1000
			|| Math.abs(ts.x - x) > 50
			|| Math.abs(ts.y - y) > 50
		)) return;
		
		const pr = this.getDPR();
		const br = this.rectCache!;
		const mw = br.width;
		const mh = br.width / this.ratio;
		this.props.onPointerDown?.((x - br.left - mw / 2) / mh, (y - br.top - mh / 2) / mh, button);
		
		const [ax, ay] = [(x - br.left) * pr, (y - br.top) * pr];
		
		if (button || !this.props.editable) return;
		
		const hit = this.sortParts(this.parts!).reverse().find(part => {
			if (!part.hitAreas) return false;
			for (const { area, crop } of part.hitAreas) {
				if (
					pointInPolygon([ax, ay], area)
					&& (!crop || pointInPolygon([ax, ay], crop))
				) return true;
			}
			return false;
		});
		
		this.props.selectPart?.(hit?.id);
		
	}
	
	getSibling() {
		if (!this.props.builder) return;
		if (this.props.builder.previews.front == this) return this.props.builder.previews.back;
		if (this.props.builder.previews.back == this) return this.props.builder.previews.front;
	}
	
	getDragFunctions() {
		const builder = this.props.builder;
		const startDrag = this.props.startDrag || builder?.startDrag.bind(builder);
		const updatePart = this.props.updatePart || maybe(builder?.manager, m => m.updatePart.bind(m));
		return { startDrag, updatePart };
	}
	
	startDrag(el: EventTarget, icx: number, icy: number) {
	
		const part = this.parts!.find(p => p.id == this.props.selected);
		if (!part) return;
		
		const { features } = this.props.product;
		if (features?.logoPositionLocked) return;
		
		const sibling = this.getSibling();
		const cctr = el as HTMLElement;
		const br = cctr.getBoundingClientRect();
		const ox = icx - br.left - br.width / 2;
		const oy = icy - br.top - br.height / 2;
		
		const cbr = this.rectCache!;
		const mh = cbr.height;
		
		let { x, y, w, h } = { x: 0, y: 0, w: 0.5, h: 0.5, ...part.point };

		const side = part.point?.side || this.props.side;
		let aid = part.point?.area || 'main';
		let area = features?.areas?.[aid];
		let aside = area?.sides[side];
		let poly = maybe(aside, getPolyForSide);
		
		const xb = this.ratio / 2; // hardcode
		const yb = 0.5;
		
		this.dragging = true;
		if (sibling) sibling.dragging = true;
		
		const { startDrag, updatePart } = this.getDragFunctions();
		startDrag?.((cx, cy) => {
		
			const px = ((cx - ox) - cbr.left - cbr.width / 2) / mh;
			const py = ((cy - oy) - cbr.top) / mh - 0.5;
			
			let nx = Math.min(xb, Math.max(-xb, px));
			let ny = Math.min(yb, Math.max(-yb, py));
			const nw = w, nh = h;
			
			if (poly && !pointInPolygon([nx, ny], poly)) {
				const narea = Object.entries(this.props.product.features!.areas!).find(([, na]) => {
					const npoly = maybe(na.sides[side], getPolyForSide);
					return npoly && pointInPolygon([nx, ny], npoly);
				});
				if (narea) {
					aid = narea[0];
					area = narea[1];
					aside = area.sides[side];
					poly = maybe(aside, getPolyForSide);
				}
				else if (aside?.type != 'rect') {
					return;
				}
			}
			
			if (aside?.type == 'rect') {
				nx = Math.max(aside.topLeft![0] + nw * 0.5, Math.min(aside.bottomRight![0] - nw * 0.5, nx));
				ny = Math.max(aside.topLeft![1] + nh * 0.5, Math.min(aside.bottomRight![1] - nh * 0.5, ny));
			}
			
			[x, y, w, h] = [nx, ny, nw, nh];
			updateSelectionRect(cctr, { x, y, w, h, mh });

			const point = { ...part.point!, x, y, area: aid };
			this.parts!.find(p => p.id == part.id)!.point = point;
			maybe(sibling?.parts?.find(p => p.id == part.id), spart => spart.point = point);
			
			this.scheduleAnimation();

		}, () => {

			this.dragging = false;
			if (sibling) sibling.dragging = false;
			updatePart?.(part.id, { point: { ...part.point!, x, y, w, h, area: aid }, position: 'custom' });
			
			this.scheduleAnimation();
			
		});
		
		this.sibling = sibling;
		this.scheduleAnimation();
		
		return true;
		
	}

	startResize(el: HTMLElement, icx: number, icy: number) {
	
		const part = maybe(this.props.selected, pid => this.props.parts[pid]);
		if (!part) return;
		
		const { features } = this.props.product;
	
		const cctr = el.parentElement!;
		const br = cctr.getBoundingClientRect();

		const cbr = this.rectCache!;
		const mh = cbr.height;

		const mx = parseInt(el.dataset.x!);
		const my = parseInt(el.dataset.y!);
		
		let { x, y, w, h } = { x: 0, y: 0, w: 0.5, h: 0.5, ...part.point };
		const iw = w, ih = h;
		
		const side = part.point?.side || this.props.side;
		const aid = part.point?.area || 'main';
		const area = features?.areas?.[aid];
		const aside = area?.sides[side];
		
		const maxAB = !this.props.skipLimits && features?.maxLogoSize;
		const [maxW, maxH] = maxAB ? ([90, 270].includes(part.text?.rotate || 0) ? [maxAB[1], maxAB[0]] : maxAB) : [Infinity, Infinity];
		const maxS = maxAB
			? (iw >= ih ? Math.min(maxAB[0] / iw, maxAB[1] / ih) : Math.min(maxAB[1] / iw, maxAB[0] / ih))
			: Infinity;
		
		const { startDrag, updatePart } = this.getDragFunctions();
		startDrag?.((cx, cy) => {
		
			const dx = cx - icx;
			const dy = cy - icy;
			
			const tw = br.width + dx * mx * 2;
			const th = br.height + dy * my * 2;
			
			let nx = part.point!.x || 0, ny = part.point!.y || 0;
			let nw: number, nh: number;
			
			if (part.type == 'name' || part.type == 'text' || part.type == 'number' || part.type == 'alias') {
				nw = Math.min(maxW, Math.max(0.1, tw / mh));
				nh = Math.min(maxH, Math.max(0.05, th / mh));
			}
			else {
				const minS = Math.min(0.035 / iw, 0.035 / ih);
				const s = Math.min(maxS, Math.max(minS, tw / br.width, th / br.height));
				[nw, nh] = fixAspectRatio(iw * s, ih * s, this.parts?.find(sp => sp.id == part.id) || part);
			}

			if (aside?.type == 'rect') {
				const br = aside.bottomRight!, tl = aside.topLeft!;
				if (nw > br[0] - tl[0]) nw = br[0] - tl[0];
				if (nh > br[1] - tl[1]) nh = br[1] - tl[1];
				const hnw = nw * 0.5, hnh = nh * 0.5;
				if (nx - hnw < tl[0]) nx = tl[0] + hnw;
				if (nx + hnw > br[0]) nx = br[0] - hnw;
				if (ny - hnh < tl[1]) ny = tl[1] + hnh;
				if (ny + hnh > br[1]) ny = br[1] - hnh;
			}
			else {
				const xb = this.ratio / 2 - (nw / 2); // hardcode
				const yb = 0.5 - (nh / 2);
				nx = Math.min(xb, Math.max(-xb, nx));
				ny = Math.min(yb, Math.max(-yb, ny));
			}
			[x, y, w, h] = [nx, ny, nw, nh];
			updateSelectionRect(cctr, { x, y, w, h, mh });
			
		}, () => {

			updatePart?.(part.id, { point: { ...part.point!, w, h, x, y }, position: 'custom' });

		});
		
	}
	
	zoomToSelection = (e: Event) => {
		e.stopPropagation();
		e.preventDefault();
		this.props.builder?.updateZoomArea(true);
	};
	resetZoom = (e: Event) => {
		e.stopPropagation();
		e.preventDefault();
		this.props.builder?.resetZoom();
	};
	
	async paintToCanvas(canvas: HTMLCanvasElement, x: number, y: number, width: number, height: number, parts: DesignParts) {
		
		while (this.updating || !this.base) await sleep(100);
		
		this.forceCanvas = canvas;
		this.forceDPR = 1;
		this.forceRect = { width, height, x, y };
		this.painted = false;
		await this.update(true, parts);
		for (let i = 0; !this.painted && i < 5; i++) {
			await sleep(100);
			await this.update();
			this.paint();
		}
		
		if (!this.painted) throw new Error(`Paint failed (imageErrors = ${this.imageErrors})`);
		
	}
	
	static async capturePreview(previews: DesignPreview[], scale: number, parts: DesignParts, format = 'png', quality = 0.9) {
	
		const product = previews[0]!.props.product;
	
		const [pw, ph] = product.features?.imageSizes?.[0] || [768, 1024];
		const [w, h] = [Math.round(pw * scale), Math.round(ph * scale)];
		
		const canvas = document.createElement('canvas');
		canvas.width = (previews.length * w);
		canvas.height = h;
		
		for (const [i, preview] of previews.entries()) {
			await preview.paintToCanvas(canvas, w * i, 0, w, h, parts);
		}
		const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, `image/${format}`, quality));
		
		return blob!;
		
	}
	
}
